import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, styled } from '@mui/material';
import RichTextEditor from '../../../../../common/RichTextEditor';
import logo from '../../../../../../assets/img/logo_with_text.png';
import { buildCoverPageHTML, escapeHtml, ReportTemplateData } from '../utils/notesTemplates.util';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  margin: theme.spacing(2),
  minHeight: '842px', // A4 height
  width: '595px', // A4 width
  backgroundColor: '#fff',
  overflow: 'hidden',
  position: 'relative',
  boxSizing: 'border-box',
  '& [contenteditable="true"]': {
    outline: 'none !important',
    '&:focus': {
      outline: 'none !important',
      background: 'transparent !important',
    },
  },
  '& .ProseMirror': {
    maxWidth: '100%',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    padding: '10px 20px',
    '& p': {
      maxWidth: '100%',
    },
    '& img.company-logo, & img[alt="CD Construction Consulting"]': {
      width: '150px !important', // Using !important to override any inline styles
      height: 'auto !important',
      display: 'block !important',
      margin: '0 auto 2rem auto !important',
      maxWidth: '150px !important',
    },
  },
}));

interface EditableCoverPageProps {
  initialData: {
    date: string;
    client: {
      name: string;
      bank: string;
      address: string;
      city: string;
      state: string;
      zip: string;
    };
    project: {
      name: string;
      number: string;
      location: string;
    };
    payment: {
      requestedAmount: string;
      contractor: string;
      application: string;
    };
    completion: {
      percentage: number;
      contract: string;
    };
    author?: {
      name: string;
      signatureUrl?: string;
      email?: string;
    };
  };
  reportData: ReportTemplateData;
  onChange: (content: string) => void;
  initialHtml?: string;
}

const EditableCoverPage: React.FC<EditableCoverPageProps> = ({ initialData, reportData, onChange, initialHtml }) => {
  const [content, setContent] = useState('');
  const signatureInitialized = useRef(false);
  const lastSignatureUrl = useRef<string | undefined>(undefined);
  const lastAuthorEmail = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Only regenerate content if:
    // 1. This is the first time (not initialized)
    // 2. The signature URL actually changed
    // 3. The author's email actually changed
    // 4. No content exists
    const signatureUrlChanged = lastSignatureUrl.current !== initialData.author?.signatureUrl;
    const authorEmailChanged = lastAuthorEmail.current !== initialData.author?.email;

    if (!signatureInitialized.current || signatureUrlChanged || authorEmailChanged || !content) {
      let baseHtml = initialHtml || '';

      // If no saved HTML, generate the base template via shared builder
      if (!baseHtml) {
        baseHtml = buildCoverPageHTML(reportData)
          .replace(
            '<img alt="CD Construction Consulting" class="company-logo" />',
            `<img src="${logo}" alt="CD Construction Consulting" class="company-logo" />`,
          )
          .trim();
      } else {
        // If we have existing HTML content, ensure the logo is present and correct
        if (!baseHtml.includes('class="company-logo"')) {
          // Replace old logo if it exists, or add new one at the top
          const logoHtml = `<div style="text-align: center; margin-bottom: 2rem;">
            <img src="${logo}" alt="CD Construction Consulting" class="company-logo" />
          </div>`;

          if (baseHtml.includes('<img src=')) {
            // Replace existing logo
            baseHtml = baseHtml.replace(/<div style="text-align: center[^>]*>[\s\S]*?<\/div>/, logoHtml);
          } else {
            // Add logo at the start of content
            baseHtml = baseHtml.replace('<div class="cover-letter">', `<div class="cover-letter">${logoHtml}`);
          }
        }
      }

      // Always remove any existing signature blocks and append current user's signature
      let updatedHtml = baseHtml;

      // Remove any existing signature blocks (the generated email paragraph is removed
      // separately via its data-signature-email marker, so it is not matched here)
      updatedHtml = updatedHtml.replace(/<p[^>]*>Respectfully,<\/p>[\s\S]*?<p[^>]*>CD Construction Consulting<\/p>/g, '');
      updatedHtml = updatedHtml.replace(/\s*<p[^>]*data-signature-email="true"[^>]*>[^<]*<\/p>/g, '');
      updatedHtml = updatedHtml.replace(/<img[^>]*alt="Signature"[^>]*>/g, '');
      // Remove only the final closing wrapper, not the first </div> in the document
      updatedHtml = updatedHtml.replace(/<\/div>\s*$/, '');

      // Append signature block
      const signatureBlock = `
            <p>Respectfully,</p>
            ${
              initialData.author?.signatureUrl
                ? `<img src="${initialData.author.signatureUrl}" alt="Signature" style="height:80px !important; width:auto !important; max-width:260px !important; max-height:80px !important;" />`
                : ''
            }
            <p style="margin-top: 1.5rem;">${initialData.author?.name || 'CDCC Representative'}</p>
            <p class="signature-company" style="margin: 0;">CD Construction Consulting</p>
            ${
              initialData.author?.email
                ? `<p style="margin: 0;" data-signature-email="true">${escapeHtml(initialData.author.email)}</p>`
                : ''
            }
          </div>
      `;

      updatedHtml += signatureBlock;
      setContent(updatedHtml);

      // Mark as initialized and update the last signature URL/email
      signatureInitialized.current = true;
      lastSignatureUrl.current = initialData.author?.signatureUrl;
      lastAuthorEmail.current = initialData.author?.email;
    }
  }, [initialData, reportData, initialHtml, initialData.author?.signatureUrl, initialData.author?.email, content]);

  const handleChange = (newContent: string) => {
    setContent(newContent);
    onChange(newContent);
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        '.ProseMirror': {
          width: '100%',
          boxSizing: 'border-box',
          padding: '0 20px !important',
          '& > div.cover-letter': {
            width: '100%',
            boxSizing: 'border-box',
            padding: '0 20px',
          },
          '& *::selection': {
            background: 'rgba(0, 0, 0, 0.1)',
          },
          '& *:focus': {
            outline: 'none !important',
            background: 'transparent !important',
          },
          '& p': {
            width: '100%',
            boxSizing: 'border-box',
            margin: '0.3em 0',
            whiteSpace: 'pre-wrap',
          },
          // Override default img styles for signature images specifically
          '& img[alt="Signature"]': {
            // The editor page is A4 in points (595x842), so 72px = 1in: keep the signature at least 1in tall.
            height: '80px !important',
            width: 'auto !important',
            maxWidth: '260px !important',
            maxHeight: '80px !important',
          },
        },
      }}
    >
      <StyledPaper elevation={2}>
        <RichTextEditor value={content} onChange={handleChange} height={842} scale={1} />
      </StyledPaper>
    </Box>
  );
};

export default EditableCoverPage;
